import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
  AlertTitle,
  LinearProgress,
  Divider,
  Checkbox,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  InsertDriveFile as FileIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import Papa from 'papaparse';
import type { ParseResult, ParseError } from 'papaparse';
import { PathfinderDataPoint } from '../../services/pathfinderApi';

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  uploadedDate: Date;
  data?: any[];
  error?: string;
  loaded?: boolean; // Track if file has been loaded to map
  visible?: boolean; // Track layer visibility
}

export type LoadFilesMode = 'as-one' | 'separate';

interface CSVUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onLoadFiles?: (files: { dataPoints: PathfinderDataPoint[]; fileName: string }[], mode: LoadFilesMode) => void; // Batch load: selected files either merged into one tab ('as-one') or one tab per file ('separate')
  onToggleVisibility?: (fileName: string, visible: boolean) => void;
  onFileUnloaded?: (fileName: string) => void;
}

const findProperty = (props: any, possibleNames: string[]): any => {
  const lowerProps: Record<string, any> = {};
  Object.keys(props).forEach(key => {
    lowerProps[key.toLowerCase()] = props[key];
  });

  for (const name of possibleNames) {
    const value = lowerProps[name.toLowerCase()];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
};

const parseCSV = async (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: ParseResult<any>) => {
        if (results.errors.length > 0) {
          reject(new Error(`CSV parsing errors: ${results.errors.map((e: ParseError) => e.message).join(', ')}`));
        } else {
          resolve(results.data);
        }
      },
      error: (error: Error) => {
        reject(error);
      },
    });
  });
};

const parseGeoJSON = async (file: File): Promise<any[]> => {
  const text = await file.text();
  const geoJsonData = JSON.parse(text);
  // Validate Structure
  if (!geoJsonData.type || geoJsonData.type !== 'FeatureCollection'){
    throw new Error('Invalid GeoJSON format: Missing or invalid "type" field');
  }
  if (!geoJsonData.features || !Array.isArray(geoJsonData.features)) {
    throw new Error('Invalid GeoJSON format: Missing or invalid "features" array');
  }
  return geoJsonData.features;
};

const parseKML = async (file: File): Promise<any[]> => {
  const text = await file.text();
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(text, 'text/xml');
  
  // Check for parsing errors
  const parseError = xmlDoc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Invalid KML file: XML parsing error');
  }
  
  const features: any[] = [];
  
  // Find all Placemarks in the KML
  const placemarks = xmlDoc.querySelectorAll('Placemark');
  
  if (placemarks.length === 0) {
    throw new Error('No Placemarks found in KML file');
  }
  
  placemarks.forEach((placemark, index) => {
    try {
      // Extract name and description
      const nameEl = placemark.querySelector('name');
      const descEl = placemark.querySelector('description');
      const name = nameEl?.textContent?.trim() || `Placemark-${index}`;
      const description = descEl?.textContent?.trim() || '';
      
      // Extract extended data if present
      const extendedData: Record<string, string> = {};
      const dataElements = placemark.querySelectorAll('ExtendedData Data, ExtendedData SimpleData');
      dataElements.forEach(dataEl => {
        const dataName = dataEl.getAttribute('name') || dataEl.getAttribute('key');
        const dataValue = dataEl.textContent?.trim();
        if (dataName && dataValue) {
          extendedData[dataName] = dataValue;
        }
      });
      
      // Also check for Schema-based extended data
      const schemaData = placemark.querySelectorAll('ExtendedData SchemaData SimpleData');
      schemaData.forEach(dataEl => {
        const dataName = dataEl.getAttribute('name');
        const dataValue = dataEl.textContent?.trim();
        if (dataName && dataValue) {
          extendedData[dataName] = dataValue;
        }
      });
      
      // Try to find timestamp in TimeStamp or TimeSpan
      let timestamp: string | null = null;
      const timeStampEl = placemark.querySelector('TimeStamp when');
      const timeSpanBegin = placemark.querySelector('TimeSpan begin');
      if (timeStampEl?.textContent) {
        timestamp = timeStampEl.textContent.trim();
      } else if (timeSpanBegin?.textContent) {
        timestamp = timeSpanBegin.textContent.trim();
      }
      
      // Extract geometry - Point
      const pointEl = placemark.querySelector('Point coordinates');
      if (pointEl?.textContent) {
        const coords = pointEl.textContent.trim().split(',').map(c => parseFloat(c.trim()));
        if (coords.length >= 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
          features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [coords[0], coords[1], coords[2] || 0]
            },
            properties: {
              name,
              description,
              timestamp,
              ...extendedData
            }
          });
        }
        return;
      }
      
      // Extract geometry - LineString
      const lineEl = placemark.querySelector('LineString coordinates');
      if (lineEl?.textContent) {
        const coordPairs = lineEl.textContent.trim().split(/\s+/);
        const coordinates = coordPairs.map(pair => {
          const [lng, lat, alt] = pair.split(',').map(c => parseFloat(c.trim()));
          return [lng, lat, alt || 0];
        }).filter(c => !isNaN(c[0]) && !isNaN(c[1]));
        
        if (coordinates.length > 0) {
          features.push({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates
            },
            properties: {
              name,
              description,
              timestamp,
              ...extendedData
            }
          });
        }
        return;
      }
      
      // Extract geometry - Polygon
      const polygonEl = placemark.querySelector('Polygon outerBoundaryIs LinearRing coordinates');
      if (polygonEl?.textContent) {
        const coordPairs = polygonEl.textContent.trim().split(/\s+/);
        const coordinates = coordPairs.map(pair => {
          const [lng, lat, alt] = pair.split(',').map(c => parseFloat(c.trim()));
          return [lng, lat, alt || 0];
        }).filter(c => !isNaN(c[0]) && !isNaN(c[1]));
        
        if (coordinates.length > 0) {
          features.push({
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [coordinates]
            },
            properties: {
              name,
              description,
              timestamp,
              ...extendedData
            }
          });
        }
        return;
      }
      
      // Extract geometry - MultiGeometry (take first geometry)
      const multiGeoPoint = placemark.querySelector('MultiGeometry Point coordinates');
      if (multiGeoPoint?.textContent) {
        const coords = multiGeoPoint.textContent.trim().split(',').map(c => parseFloat(c.trim()));
        if (coords.length >= 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
          features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [coords[0], coords[1], coords[2] || 0]
            },
            properties: {
              name,
              description,
              timestamp,
              ...extendedData
            }
          });
        }
      }
    } catch (err) {
      console.warn(`Failed to parse Placemark ${index}:`, err);
    }
  });
  
  if (features.length === 0) {
    throw new Error('No valid geometries found in KML file');
  }
  
  return features;
};

const calculateCentroid = (coordinates: number[][][]): [number, number] => {
  const points = coordinates[0]; // Outer ring
  let sumLng = 0;
  let sumLat = 0;
  const numPoints = points.length - 1; // Last point is same as first

  for (let i = 0; i < numPoints; i++) {
    sumLng += points[i][0];
    sumLat += points[i][1];
  }
  return [sumLng / numPoints, sumLat / numPoints];
};

const extractArcGISGraphicInfo = (graphic: any): any => {
  // Try to extract text/label from attributes
  if (graphic.attributes) {
    const attrs = graphic.attributes;
    
    // Common text field names
    const textFields = ['text', 'name', 'label', 'title', 'description', 'NAME', 'Text', 'Label'];
    for (const field of textFields) {
      if (attrs[field] !== undefined && attrs[field] !== null && attrs[field] !== '') {
        return attrs[field];
      }
    }
    
    // If no text field, return a simplified version of attributes
    const simpleAttrs: any = {};
    for (const [key, value] of Object.entries(attrs)) {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        simpleAttrs[key] = value;
      }
    }
    
    if (Object.keys(simpleAttrs).length > 0) {
      return simpleAttrs;
    }
  }
  
  // Fallback
  return '[ArcGIS Graphic]';
};

// Recursive function to safely serialize any value for React rendering
const serializeValue = (value: any, depth: number = 0): any => {
  // Prevent infinite recursion
  if (depth > 10) {
    return '[Deeply nested object]';
  }

  // Handle null/undefined
  if (value === null || value === undefined) {
    return value;
  }
  
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  
  // Handle Date objects
  if (value instanceof Date) {
    return value.toISOString();
  }
  
  // Handle arrays - recursively serialize each element
  if (Array.isArray(value)) {
    return value.map(item => serializeValue(item, depth + 1));
  }
  
  // Handle objects
  if (typeof value === 'object') {
    // Check for ArcGIS Graphic objects specifically
    if (value.geometry || value.symbol || value.attributes || value.popupTemplate) {
      // Extract useful information from ArcGIS Graphic
      return extractArcGISGraphicInfo(value);
    }
    
    // Check for ArcGIS Geometry objects (has spatialReference, rings, paths, points, etc.)
    if (value.spatialReference && (value.rings || value.paths || value.points || value.x !== undefined)) {
      return '[ArcGIS Geometry]';
    }
    
    // For regular objects, recursively serialize all properties
    const serialized: any = {};
    for (const [key, val] of Object.entries(value)) {
      serialized[key] = serializeValue(val, depth + 1);
    }
    return serialized;
  }
  
  // Fallback for any other type
  return String(value);
};

const normalizeData = (
  rawData: any[],
  format: 'csv' | 'geojson',
  fileName: string
): any[] => {
  const missingFieldsSet = new Set<string>();

  const normalized = rawData.map((item, index) => {
    let latitude: number = 0;
    let longitude: number = 0;
    let properties: any = {};
    let geometryType: string = 'Point';
    let fullGeometry: any = null;

    if (format === 'csv') {
      latitude = parseFloat(findProperty(item, ['latitude', 'lat', 'y', 'lat_deg', 'lat_decimal']));
      longitude = parseFloat(findProperty(item, ['longitude', 'lon', 'lng', 'long', 'x']));
      properties = { ...item };
    } 
    else if (format === 'geojson') {
      const geometry = item.geometry;
      geometryType = geometry.type;
      fullGeometry = geometry; 
      properties = item.properties || {};

       // Handle different geometry types
      let coordinates: [number, number] | null = null;

      if (geometry.type === 'Point' && geometry.coordinates) {
        coordinates = [geometry.coordinates[0], geometry.coordinates[1]];
      } 
      else if (geometry.type === 'MultiPoint' && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0) {
        coordinates = [geometry.coordinates[0][0], geometry.coordinates[0][1]];
      } 
      else if (geometry.type === 'LineString' && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0) {
        coordinates = [geometry.coordinates[0][0], geometry.coordinates[0][1]];
      } 
      else if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0 && geometry.coordinates[0].length > 0) {
        coordinates = calculateCentroid(geometry.coordinates);
      }
      else if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0 && geometry.coordinates[0].length > 0 && geometry.coordinates[0][0].length > 0) {
        coordinates = calculateCentroid(geometry.coordinates[0]);
      }
      else if (geometry.type === 'MultiLineString' && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0 && geometry.coordinates[0].length > 0) {
        coordinates = [geometry.coordinates[0][0][0], geometry.coordinates[0][0][1]];
      }

      if (!coordinates) {
        throw new Error(`Unsupported or invalid geometry type: ${geometry.type} at feature index ${index}`);
      }
      longitude = coordinates[0];
      latitude = coordinates[1];
    }
    
    // Recursively serialize all properties to handle any nested complexity 
    const serializedProperties = serializeValue(properties);
    
    // Extract fields from SERIALIZED properties
    const timestamp = findProperty(serializedProperties, ['timestamp', 'time', 'date']);
    const deviceId = findProperty(serializedProperties, ['deviceId', 'device_id', 'registration_id', 'registration id', 'id', 'device']);
    const accuracy = findProperty(serializedProperties, ['accuracy', 'acc', 'accuracy (m)', 'accuracy_m']);
    const deviceType = findProperty(serializedProperties, ['deviceType', 'device_type', 'type']); 
    const speed = findProperty(serializedProperties, ['speed', 'velocity', 'speed_kmh', 'speed_mph', 'spd']);
    const altitude = findProperty(serializedProperties, ['altitude', 'alt', 'elevation']);    

    if (!timestamp) missingFieldsSet.add('timestamp');
    if (!deviceId) missingFieldsSet.add('deviceId (using auto generated)');

    return {
      longitude,
      latitude,
      timestamp: timestamp || new Date().toISOString(),
      deviceId: String(deviceId || `${fileName.replace(/\.(csv|geojson|json)$/i, '')}-point-${index}`),
      accuracy: accuracy || null,
      deviceType: String(deviceType || 'phone'),
      speed: speed || null,
      altitude: altitude || null,
      ...serializedProperties,
      _fileFormat: format,
      _originalIndex: index,
      _geometryType: geometryType,
      _fullGeometry: fullGeometry,
    };
  });
  
  if (missingFieldsSet.size > 0) {
    console.warn(`Note: some fields not found in ${fileName}:`, Array.from(missingFieldsSet).join(', '));
  }
  return normalized;
};

const CSVUploadDialog: React.FC<CSVUploadDialogProps> = ({ open, onClose, onLoadFiles, onToggleVisibility, onFileUnloaded }) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleFileUnload = (event: CustomEvent) => {
      // Check if we should clear all files (Uploaded Data tab was closed)
      if (event.detail.clearAll) {
        setUploadedFiles(prev => prev.map(f => ({ ...f, loaded: false, visible: false })));
        onFileUnloaded?.('all');
        return;
      }
      
      const fileName = event.detail.fileName;
      
      // Reset the file's loaded state
      setUploadedFiles(prev => prev.map(f => 
        f.name === fileName ? { ...f, loaded: false, visible: false } : f
      ));
      
      // Notify parent if needed
      onFileUnloaded?.(fileName);
    };

    window.addEventListener('csv-file-unloaded', handleFileUnload as EventListener);
    return () => {
      window.removeEventListener('csv-file-unloaded', handleFileUnload as EventListener);
    };
  }, [onFileUnloaded]);

  const handleToggleVisibility = useCallback((fileId: string) => {
    setUploadedFiles(prev => prev.map(f => {
      if (f.id === fileId && f.loaded) {
        const newVisible = !f.visible;
        if (onToggleVisibility) {
          onToggleVisibility(f.name, newVisible);
        }
        return { ...f, visible: newVisible };
      }
      return f;
    }));
  }, [onToggleVisibility]);

  const handleToggleFileSelection = useCallback((fileId: string, selected: boolean) => {
  setSelectedFileIds(prev => {
    const next = new Set(prev);
    if (selected) next.add(fileId); else next.delete(fileId);
    return next;
  });
}, []);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setLoading(true);
    setError(null);
    setProgress(0);

    const newFiles: UploadedFile[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = `${file.name}-${Date.now()}-${i}`;

      try {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        if (!fileExtension) {
          throw new Error(`Could not determine file extension for ${file.name}`);
        }

        let rawData: any[] = [];
        let format: 'csv' | 'geojson';

        if (fileExtension === 'csv') {
          rawData = await parseCSV(file);
          format = 'csv';
        }
        else if (fileExtension === 'geojson' || fileExtension === 'json') {
          rawData = await parseGeoJSON(file);
          format = 'geojson'; 
        }
        else if (fileExtension === 'kml') {
          rawData = await parseKML(file);
          format = 'geojson'; // KML is converted to GeoJSON format
        } else {
          throw new Error(`Unsupported file type ${fileExtension}. Supported types are CSV, GeoJSON, and KML.`);
        }
      const parsedData = normalizeData(rawData, format, file.name);

        newFiles.push({
          id: fileId,
          file,
          name: file.name,
          size: file.size,
          uploadedDate: new Date(),
          data: parsedData,
        });

        setProgress(((i + 1) / files.length) * 100);
      } catch (err: any) {
        newFiles.push({
          id: fileId,
          file,
          name: file.name,
          size: file.size,
          uploadedDate: new Date(),
          error: err.message || 'Failed to parse CSV',
        });
        setError(`Error parsing ${file.name}: ${err.message}`);
      }
    }

    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setLoading(false);
    event.target.value = ''; // Reset input
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const fileToDataPoints = useCallback((file: UploadedFile): { dataPoints: PathfinderDataPoint[] } | { error: string } => {
  if (!file.data || file.data.length === 0) {
    return { error: 'No data found in file' };
  }
  try {
    const firstRow = file.data[0];
    const isGeoJSON = firstRow._fileFormat === 'geojson';
    let latColumn = '';
    const latOptions = ['latitude', 'lat', 'y', 'lat_deg', 'lat_decimal'];
    for (const opt of latOptions) {
      const found = Object.keys(firstRow).find(k => k.toLowerCase().trim() === opt);
      if (found) { latColumn = found; break; }
    }
    let lngColumn = '';
    const lngOptions = ['longitude', 'lon', 'lng', 'long', 'x', 'lon_deg', 'lng_decimal', 'long_decimal'];
    for (const opt of lngOptions) {
      const found = Object.keys(firstRow).find(k => k.toLowerCase().trim() === opt);
      if (found) { lngColumn = found; break; }
    }
    if (!isGeoJSON && (!latColumn || !lngColumn)) {
      return { error: `Could not detect coordinate columns. Found: ${Object.keys(firstRow).join(', ')}` };
    }
    const uploadTime = new Date();
    const dataPoints: PathfinderDataPoint[] = file.data.map((row: any, index: number) => {
      let lat: number, lng: number;
      if (isGeoJSON) {
        lat = parseFloat(row.latitude);
        lng = parseFloat(row.longitude);
      } else {
        lat = parseFloat(row[latColumn]);
        lng = parseFloat(row[lngColumn]);
      }
      const timestampRaw = row.timestamp || row.time || row.Timestamp || row.Time || row.date || row.Date;
      let timestamp = uploadTime;
      if (timestampRaw) {
        try {
          timestamp = new Date(timestampRaw);
          if (isNaN(timestamp.getTime())) timestamp = uploadTime;
        } catch { timestamp = uploadTime; }
      }
      const deviceId = row.deviceId || row.device_id || row.registration_id || row.id || row.ID || row.device || row.Device || `device-${index}`;
      const studentId = row.studentId || row.student_id || row.entity_id || row.entityId || row.user_id || row.userId || deviceId;
      const accuracy = parseFloat(row.accuracy || row.Accuracy || row.acc || '10');
      const deviceType = row.deviceType || row.device_type || row.type || row.Type || 'phone';
      if (isNaN(lat) || isNaN(lng)) {
        throw new Error(`Invalid coordinates at row ${index + 1}: lat=${lat}, lng=${lng}`);
      }
      const { _fullGeometry, _geometryType, _fileFormat, _originalIndex, longitude, latitude, ...cleanRow } = row;
      return {
        id: `${file.name}-${index}`,
        coordinates: [lat, lng] as [number, number],
        timestamp,
        studentId,
        deviceId,
        deviceType,
        accuracy: isNaN(accuracy) ? 10 : accuracy,
        speed: parseFloat(row.speed || row.Speed || '0') || undefined,
        altitude: parseFloat(row.altitude || row.Altitude || row.alt || '0') || undefined,
        source: 'csv-upload',
        metadata: {
          ...cleanRow,
          originalFileName: file.name,
          detectedLatColumn: isGeoJSON ? undefined : latColumn,
          detectedLngColumn: isGeoJSON ? undefined : lngColumn,
          timestampSource: timestampRaw ? (isGeoJSON ? 'geojson' : 'csv') : 'upload-time',
          geometryType: _geometryType,
        },
        ...(isGeoJSON && (row as any)._fullGeometry ? { _geometry: (row as any)._fullGeometry, _geometryType: (row as any)._geometryType } : {}),
      };
    });
    return { dataPoints };
  } catch (err: any) {
    return { error: err.message || 'Error processing file' };
  }
}, []);

  const handleLoadAsOne = useCallback(() => {
    const toLoad = uploadedFiles.filter(f => selectedFileIds.has(f.id) && !f.loaded && !f.error);
    if (toLoad.length === 0 || !onLoadFiles) return;
    const filesData: { dataPoints: PathfinderDataPoint[]; fileName: string }[] = [];
    for (const file of toLoad) {
      const result = fileToDataPoints(file);
      if ('error' in result) {
        setError(result.error);
        return;
      }
      filesData.push({ dataPoints: result.dataPoints, fileName: file.name });
    }
    setError(null);
    onLoadFiles(filesData, 'as-one');
    setUploadedFiles(prev => prev.map(f =>
      toLoad.some(t => t.id === f.id) ? { ...f, loaded: true, visible: true } : f
    ));
    setSelectedFileIds(prev => {
      const next = new Set(prev);
      toLoad.forEach(f => next.delete(f.id));
      return next;
    });
  }, [uploadedFiles, selectedFileIds, fileToDataPoints, onLoadFiles]);

  const handleLoadSeparate = useCallback(() => {
    const toLoad = uploadedFiles.filter(f => selectedFileIds.has(f.id) && !f.loaded && !f.error);
    if (toLoad.length === 0 || !onLoadFiles) return;
    const filesData: { dataPoints: PathfinderDataPoint[]; fileName: string }[] = [];
    for (const file of toLoad) {
      const result = fileToDataPoints(file);
      if ('error' in result) {
        setError(result.error);
        return;
      }
      filesData.push({ dataPoints: result.dataPoints, fileName: file.name });
    }
    setError(null);
    onLoadFiles(filesData, 'separate');
    setUploadedFiles(prev => prev.map(f =>
      toLoad.some(t => t.id === f.id) ? { ...f, loaded: true, visible: true } : f
    ));
    setSelectedFileIds(prev => {
      const next = new Set(prev);
      toLoad.forEach(f => next.delete(f.id));
      return next;
    });
  }, [uploadedFiles, selectedFileIds, fileToDataPoints, onLoadFiles]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const totalSize = uploadedFiles.reduce((sum, file) => sum + file.size, 0);
  const maxTotalSize = 100 * 1024 * 1024; // 100MB
  const maxFiles = 10;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <UploadIcon />
          <Typography variant="h6">Upload Dataset</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Data Classification Warning */}
        <Alert 
          severity="warning" 
          icon={<SecurityIcon />}
          sx={{ 
            mb: 3, 
            '& .MuiAlert-message': { width: '100%' },
            border: '1px solid',
            borderColor: 'warning.main',
          }}
        >
          <AlertTitle sx={{ fontWeight: 'bold' }}>
            Data Classification Notice
          </AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>The OSINT Explorer is approved only for unclassified, non-CUI data.</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Uploading or entering Classified Information or Controlled Unclassified Information (CUI) is <strong>strictly prohibited</strong>.
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 1.5, color: 'text.secondary' }}>
            By continuing, you acknowledge that all data you submit is authorized for use on this system and complies with applicable federal, DoD, and organizational data-handling policies.
          </Typography>
        </Alert>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Supported formats: CSV, GeoJSON, and KML | Max: {maxFiles} files | Total: 100MB
          </Typography>
          <Typography variant="body2" color="primary" gutterBottom>
            Current usage: {uploadedFiles.length}/{maxFiles} files, {formatFileSize(totalSize)}/100MB
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
              Uploading... {progress.toFixed(0)}%
            </Typography>
          </Box>
        )}

        {/* Upload Button */}
        <Button
          variant="contained"
          component="label"
          startIcon={<UploadIcon />}
          fullWidth
          disabled={loading || uploadedFiles.length >= maxFiles || totalSize >= maxTotalSize}
          sx={{ mb: 3, py: 1.5 }}
        >
          Upload File
          <input
            type="file"
            hidden
            multiple
            accept=".csv,.geojson,.json,.kml"
            onChange={handleFileChange}
          />
        </Button>

        {/* Load selected as one tab or separate tabs */}
          {uploadedFiles.some(f => !f.error && !f.loaded) && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="medium"
                fullWidth
                disabled={loading || selectedFileIds.size === 0 || !uploadedFiles.some(f => selectedFileIds.has(f.id) && !f.loaded && !f.error)}
                onClick={handleLoadAsOne}
              >
                Load Together
              </Button>
              <Button
                variant="outlined"
                size="medium"
                fullWidth
                disabled={loading || selectedFileIds.size === 0 || !uploadedFiles.some(f => selectedFileIds.has(f.id) && !f.loaded && !f.error)}
                onClick={handleLoadSeparate}
              >
                Load Seperately
              </Button>
            </Box>
          )}

        {/* Uploaded Files List */}
        {uploadedFiles.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Uploaded Files ({uploadedFiles.length})
            </Typography>
            <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
              {uploadedFiles.map((file) => (
                <ListItem
                  key={file.id}
                  sx={{ alignItems: 'flex-start' }}
                  secondaryAction={
                    <Box>
                    {!file.error && !file.loaded && (
                      <Checkbox
                        size="small"
                        checked={selectedFileIds.has(file.id)}
                        onChange={(e) => handleToggleFileSelection(file.id, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ mr: 0.5, pt: 0.5 }}
                      />
                    )}
                      {!file.error && file.loaded && (
                        <IconButton
                          edge="end"
                          aria-label="toggle visibility"
                          onClick={() => handleToggleVisibility(file.id)}
                          color={file.visible ? "primary" : "default"}
                          sx={{ mr: 1 }}
                        >
                          {file.visible ? <ViewIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      )}
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleRemoveFile(file.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemIcon>
                    <FileIcon color={file.error ? 'error' : file.loaded ? 'success' : 'primary'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={
                      <>
                        {file.error ? (
                          <Typography variant="caption" color="error">
                            {file.error}
                          </Typography>
                        ) : (
                          <>
                            Uploaded: {file.uploadedDate.toLocaleDateString()} | {formatFileSize(file.size)}
                            {file.data && ` | ${file.data.length} rows`}
                            {file.loaded && (
                              <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                                • Loaded {file.visible ? '(Visible)' : '(Hidden)'}
                              </Typography>
                            )}
                          </>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CSVUploadDialog;

